import { useState, useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { SplashScreen } from "@/components/SplashScreen";
import { InstallPromptModal } from "@/components/pwa/InstallPromptModal";
import { OnboardingWrapper } from "@/components/onboarding/OnboardingWrapper";

// Lazy load all pages except Index (critical path)
import Index from "./pages/Index";
const Auth = lazy(() => import("./pages/Auth"));
const Profile = lazy(() => import("./pages/Profile"));
const Favorites = lazy(() => import("./pages/Favorites"));
const AddPlace = lazy(() => import("./pages/AddPlace"));
const AddBusiness = lazy(() => import("./pages/AddBusiness"));
const Admin = lazy(() => import("./pages/Admin"));
const Analytics = lazy(() => import("./pages/admin/Analytics"));
const SimpleAnalytics = lazy(() => import("./pages/admin/SimpleAnalytics"));
const AnalyticsPlaces = lazy(() => import("./pages/admin/AnalyticsPlaces"));
const AnalyticsPlaceDetail = lazy(() => import("./pages/admin/AnalyticsPlaceDetail"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Install = lazy(() => import("./pages/Install"));
const PlacePage = lazy(() => import("./pages/PlacePage"));
const Rewards = lazy(() => import("./pages/Rewards"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => {
  const [showSplash, setShowSplash] = useState(true);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  // Initialize theme from localStorage on app start
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  // Check if we should show install prompt (1 time out of 10)
  useEffect(() => {
    // Wait for splash to finish
    if (showSplash) return;

    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      localStorage.setItem('jewmap_pwa_installed', 'true');
      return;
    }

    // Check if user said "never ask"
    if (localStorage.getItem('jewmap_never_ask_install') === 'true') {
      return;
    }

    // Check if already installed
    if (localStorage.getItem('jewmap_pwa_installed') === 'true') {
      return;
    }

    // Increment app opens counter
    const currentOpens = parseInt(localStorage.getItem('jewmap_app_opens') || '0', 10);
    const newOpens = currentOpens + 1;
    localStorage.setItem('jewmap_app_opens', newOpens.toString());

    // Show prompt 1 time out of 10 (starting at 10th open)
    if (newOpens >= 10 && newOpens % 10 === 0) {
      // Small delay to ensure smooth UX after splash
      setTimeout(() => setShowInstallPrompt(true), 500);
    }
  }, [showSplash]);

  if (showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} />;
  }

  return (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <InstallPromptModal 
          open={showInstallPrompt} 
          onOpenChange={setShowInstallPrompt} 
        />
        <OnboardingWrapper />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/favorites" element={<Favorites />} />
              <Route path="/add-place" element={<AddPlace />} />
              <Route path="/add-business" element={<AddBusiness />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/analytics" element={<SimpleAnalytics />} />
              <Route path="/admin/analytics-old" element={<Analytics />} />
              <Route path="/admin/analytics/places" element={<AnalyticsPlaces />} />
              <Route path="/admin/analytics/place/:id" element={<AnalyticsPlaceDetail />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/install" element={<Install />} />
              <Route path="/place/:id" element={<PlacePage />} />
              <Route path="/rewards" element={<Rewards />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  );
};

export default App;
